/**
 * DialogFrota.ts — Dialog flutuante posicionado perto do planeta destino.
 *
 * Fase 6: aceita VARIAS origens. O slider e o total; cada origem manda a mesma
 * fracao (pct) das naves que tem. Atalhos 25/50/75/100%, Enter envia.
 */

import { corHex } from '../config';
import type { EstadoJogo } from '../domain/tipos';
import type { Camera } from '../render/Camera';
import type { PixiApp } from '../render/PixiApp';

export interface EnvioFrota {
  origens: number[];
  destinoId: number;
  quantidade: number;   // total pedido (exato quando ha 1 origem)
  pct: number;          // fracao do disponivel (usada com varias origens)
}

export class DialogFrota {
  private readonly elemento: HTMLElement;
  private readonly elOrigem: HTMLElement;
  private readonly elDestino: HTMLElement;
  private readonly elSlider: HTMLInputElement;
  private readonly elQtd: HTMLElement;
  private readonly elEnviar: HTMLButtonElement;
  private readonly elCancelar: HTMLButtonElement;

  private origens: number[] = [];
  private destinoId: number | null = null;
  private navesMax = 0;

  private aoEnviar: ((envio: EnvioFrota) => void) | null = null;
  private aoCancelar: (() => void) | null = null;

  constructor(
    private readonly estado: EstadoJogo,
    private readonly camera: Camera,
    private readonly pixi: PixiApp,
  ) {
    this.elemento = obrigatorio('swDialogLancar');
    this.elOrigem = obrigatorio('swDialogOrigem');
    this.elDestino = obrigatorio('swDialogDestino');
    this.elSlider = obrigatorio('swDialogSlider') as HTMLInputElement;
    this.elQtd = obrigatorio('swDialogQtd');
    this.elEnviar = obrigatorio('swDialogEnviar') as HTMLButtonElement;
    this.elCancelar = obrigatorio('swDialogCancelar') as HTMLButtonElement;

    this.elSlider.addEventListener('input', () => this.atualizarQtd());
    document.querySelectorAll<HTMLButtonElement>('.sw-dialog-pct-btn').forEach((b) => {
      b.addEventListener('click', () => {
        const pct = parseFloat(b.dataset.pct || '0');
        this.elSlider.value = String(Math.max(1, Math.ceil(this.navesMax * pct)));
        this.atualizarQtd();
      });
    });
    this.elEnviar.addEventListener('click', () => this.enviar());
    this.elCancelar.addEventListener('click', () => this.fechar());
  }

  /** Registra callback chamado ao clicar Enviar. */
  onEnviar(cb: (envio: EnvioFrota) => void): void {
    this.aoEnviar = cb;
  }

  /** Registra callback chamado quando o dialog fecha (cancelar/enviar). */
  onFechar(cb: () => void): void {
    this.aoCancelar = cb;
  }

  visivel(): boolean {
    return this.elemento.classList.contains('visivel');
  }

  abrir(origens: number[], destinoId: number): void {
    const destino = this.estado.planetas.get(destinoId);
    if (!destino) return;

    // Quantas naves tenho orbitando cada origem
    const validas: number[] = [];
    let disponiveis = 0;
    for (const origemId of origens) {
      const origem = this.estado.planetas.get(origemId);
      if (!origem || origem.donoId !== this.estado.suaCor || origemId === destinoId) continue;
      let n = 0;
      for (const nave of this.estado.naves.values()) {
        if (nave.donoId === this.estado.suaCor && nave.contextId === origemId && nave.estado !== 1) n++;
      }
      if (n > 0) { validas.push(origemId); disponiveis += n; }
    }
    if (disponiveis < 1 || validas.length === 0) return;

    this.origens = validas;
    this.destinoId = destinoId;
    this.navesMax = disponiveis;

    // Posicionar perto do destino
    const tela = this.camera.mundoParaTela(destino.x, destino.y);
    const vp = this.pixi.viewport;
    const sx = Math.max(160, Math.min(vp.w - 160, tela.x));
    const sy = Math.max(120, Math.min(vp.h - 120, tela.y));
    this.elemento.style.left = `${sx}px`;
    this.elemento.style.top = `${sy}px`;
    this.elemento.style.setProperty('--sw-cor-jogador', corHex(this.estado.suaCor));

    this.elOrigem.textContent = validas.length === 1
      ? `P${validas[0]} (${disponiveis})`
      : `${validas.length} planetas (${disponiveis})`;
    const donoLabel = destino.donoId === 0
      ? 'neutro'
      : destino.donoId === this.estado.suaCor
        ? 'seu'
        : 'inimigo';
    this.elDestino.textContent = `P${destinoId} (${destino.naves} ${donoLabel})`;

    this.elSlider.max = String(disponiveis);
    this.elSlider.value = String(Math.max(1, Math.round(disponiveis * this.estado.pctEnvio)));
    this.atualizarQtd();

    this.elemento.classList.add('visivel');
  }

  fechar(): void {
    this.elemento.classList.remove('visivel');
    if (this.aoCancelar) this.aoCancelar();
  }

  /** Confirma o envio com o valor atual do slider (botao Enviar ou tecla Enter). */
  enviar(): void {
    if (this.destinoId === null || this.origens.length === 0) return;
    const qtd = Math.max(1, Math.min(this.navesMax, parseInt(this.elSlider.value, 10) || 1));
    if (this.aoEnviar) {
      this.aoEnviar({
        origens: this.origens,
        destinoId: this.destinoId,
        quantidade: qtd,
        pct: this.navesMax > 0 ? qtd / this.navesMax : 1,
      });
    }
    this.fechar();
  }

  private atualizarQtd(): void {
    this.elQtd.textContent = this.elSlider.value;
  }
}

function obrigatorio(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Elemento #${id} nao encontrado no DOM`);
  return el;
}
